import React, { Component } from "react"
import { connect } from "react-redux"
import trans from "trans"

import opentype from 'opentype.js'

import {
    Grid,
    GridCell,
    css,
    Toggler,
    Icon,
    Theme,
    Message,
    InputGroup,
} from "@bdfx/mergado-ui-kit"

import ImagickFontSelect, { ImagickFont } from "../components/imagickFontSelect";
import TextWithElementsForm from "./text-with-elements-form";

import { getFormName, getTemplateSize} from "../forms/helpers";

import {
    TEXT_ORIENTATION_HORIZONTAL,
    TEXT_ORIENTATION_VERTICAL,
} from "shape-entities/render-text";

import { find, debounce } from "lodash"

import Shape, {
    BADGE1_TYPE, BADGE_TYPE, BITMAP_TYPE, BRUSH_TYPE, CIRCLE_TEXT_TYPE, CIRCLE_TYPE, CURVED_TEXT_TYPE, CUSTOM_SET_TYPE, DASHED_FRAME_TYPE,
    DYNAMIC_TEXT_TYPE, ELLIPSE_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE, FRAME_TYPE, IMAGE_FROM_ELEMENTS_TYPE, IMAGE_TYPE, RATING_TYPE, RECTANGLE_TYPE,
    TEXT_INSIDE_SHAPE_TYPE, TEXT_SHAPE_TYPES, TEXT_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE
} from "shape-entities/types/shape";

import { BrushFormType, Gradient, Project } from "app/types/model-types";
import calcBackgroundSize from "shape-edit/helpers/calcBackgroundSize";
import { RandomProduct } from "shape-edit/types"
import { selectFormValues, updateInputValue as change, initForm } from "app/reducers/form"
import { ID, intboolean } from "app/types/app-types";
import { createToast, setEditMode } from "actions";
import { ImagickFontsType } from "app/reducers/imagickFontsReduxList";
import { TEXT_WITH_ELEMENTS_TYPE } from '../../shape-entities/types/shape';
import { Slider } from "@mui/material"
import { iconDisabledColor } from "template-edit/components/tools-panel"
import ReflectionForm from "./reflection-form"
import ShadowForm from "./shadow-form"
import TextOptionsForm from "./text-options-form"
import XmlElementSelectForm from "./xml-element-select-form"
import XmlElementFormatterForm from "./xml-element-formatter-form"
import TextContentForm from "./text-content-form"
import ShapeStrokeLinesAndColorsForm from "./shape-stroke-lines-and-colors-form"
import ShapePositionSizeAndRotationForm from './shape-position-size-and-rotation-form';
import TextOptionsTwoForm from './text-options-two-form';
import { capitalize } from "lodash"
import { DEFAULT_ASPECT_RATIO_TYPES } from "shape-entities/defaults"
import { getRatingShapeFontSize, testUsers } from "template-edit/components/helper"
import ShapeStrokeForm from "./shape-stroke-form"
import { TemplateOrAlternativeTemplateType } from "template-edit/undoRedoReducers"
import RatingForm from "./rating-form"
import RatingShape from "shape-entities/types/Rating"
import { NumericInput } from "./fields"
import ImageFromElementForm from "./image-from-element-form"
import { checkDepotsEnabled } from "app/helpers/api"
import { AxiosResponse } from "axios"

const { min, max, abs, sin, cos, PI, round, atan2, sqrt, ceil } = Math

interface Props {
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    activeShapeId: string
    brushForm: BrushFormType
    randomProduct: RandomProduct
    project: Project
    imagickFonts: ImagickFontsType
    change: typeof change
    initForm: typeof initForm
    createToast: typeof createToast
    setEditMode: typeof setEditMode
}

interface State {
    missingGlyphs: string[]
    showDepotsWarning: boolean
}

class CommonShapeForm extends Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            missingGlyphs: null,
            showDepotsWarning: null,
        }
    }

    componentDidMount(): void {
        this.checkMissingGlyphs()
        this.checkDepots(this.props.activeShapeId && this.props.form?.shapes[this.props.activeShapeId]?.rating_from_depots)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const prevForm = prevProps && typeof prevProps.isAlt !== 'undefined' ? prevProps?.form : null
        const thisForm = this.props.form

        const thisActiveShape = this.props.activeShapeId && thisForm.shapes && thisForm.shapes[this.props.activeShapeId]
        const prevActiveShape = prevProps.activeShapeId && prevForm.shapes && prevForm.shapes[prevProps.activeShapeId]

        if (thisActiveShape && prevActiveShape) {
            if (thisActiveShape.rating_from_depots !== prevActiveShape.rating_from_depots) {
                this.checkDepots(thisActiveShape?.rating_from_depots)
            } else if (
                (thisActiveShape.text_fontfamily !== prevActiveShape.text_fontfamily) ||
                (thisActiveShape.text_content !== prevActiveShape.text_content)
            ) {
                this.checkMissingGlyphs()
            }
        }
    }

    checkDepots = (isRatingFromDepots: intboolean) => {
        if (isRatingFromDepots) {
            checkDepotsEnabled()
                .then((response: AxiosResponse<any>) => {
                    this.setState({ showDepotsWarning: !(response.data.is_enabled || null) })
                })
                .catch((error) => {
                    console.error('cannot check depots enabled', error)
                    this.setState({ showDepotsWarning: true })
                })
        } else {
            this.setState({ showDepotsWarning: false })
        }
    }

    handleChangeGradient = (gradient: Gradient, shape: Shape) => {
        const { form, isAlt } = this.props

        const formName = getFormName(isAlt)

        if (typeof shape !== "undefined") {
            this.props.change(formName, `shapes.${shape.index}`, {
                ...shape,
                gradient: gradient,
                gradient_id: gradient?.id ? gradient.id : null,
            })
        } else {
            this.props.initForm(formName, { ...form, gradient: gradient, gradient_id: gradient.id })
        }
    }

    updateShapeSizeDebounced = debounce((subFormName: string, activeShapeId: ID, keepSizeAsPossible: boolean = false, changeTextOrientation: boolean = false) => {
        this.updateShapeSize(subFormName, activeShapeId, keepSizeAsPossible, changeTextOrientation)
    }, 200)

    updateShapeSize = (subFormName: string, activeShapeId: ID, keepSizeAsPossible: boolean, changeTextOrientation: boolean) => {
        const { form, isAlt } = this.props

        const formName = getFormName(isAlt)
        const shape: Shape = form.shapes[activeShapeId]
        const newSize = calcBackgroundSize(shape, shape.text_content)
        const templateSize = getTemplateSize(form)

        const isAspectSizeRatio = shape.is_aspect_size_ratio || DEFAULT_ASPECT_RATIO_TYPES.includes(shape.type)

        const isOverflowX = newSize.width >= .9 * templateSize.width
        const isOverflowY = newSize.height >= .9 * templateSize.height

        let newShapeWidth: number = isOverflowX ? templateSize.width : (keepSizeAsPossible && shape.width > newSize.width ? shape.width : newSize.width)
        let newShapeHeight: number = isOverflowY ? templateSize.height : (keepSizeAsPossible && shape.height > newSize.height ? shape.height : newSize.height)

        let newFontSize: number = shape.text_fontsize
        let newTextPadding: number = (shape.text_padding || 0)
        let newCornerRadius: number = (shape.corner_radius || 0)
        let textWrap: boolean = isOverflowX ? true : shape.text_wrap

        let paddingX = 0
        let paddingY = 0

        if ((keepSizeAsPossible || isAspectSizeRatio) && shape.height && shape.width) {
            const scaleX = newSize.width / shape.width
            const scaleY = newSize.height / shape.height
            const maxScale = max(scaleX, scaleY)

            if (keepSizeAsPossible) {
                const oldShape: Shape = {
                    ...shape,
                    width: shape.width,
                    height: shape.height,
                    text_orientation: changeTextOrientation ? (shape.text_orientation === TEXT_ORIENTATION_HORIZONTAL ? TEXT_ORIENTATION_VERTICAL : TEXT_ORIENTATION_HORIZONTAL) : null
                }

                const oldTextSize = calcBackgroundSize(oldShape, shape.text_content)

                paddingX = newSize.width - oldTextSize.width
                paddingY = newSize.height - oldTextSize.height
            }

            if (maxScale > 1 || (keepSizeAsPossible && paddingX && paddingY)) { //only zoom in
                if (keepSizeAsPossible) {
                    if (paddingX > 0 && paddingY > 0) {
                        newShapeWidth = newShapeWidth + paddingX
                        newShapeHeight = newShapeHeight + paddingY
                    } else {
                        newShapeWidth = shape.width * maxScale
                        newShapeHeight = shape.height * maxScale
                    }
                } else {
                    newShapeWidth = shape.width * maxScale
                    newShapeHeight = shape.height * maxScale
                }
            }
        }

        if (isAspectSizeRatio) {
            const maxSide = max(newShapeWidth, newShapeHeight)
            newShapeWidth = maxSide
            newShapeHeight = maxSide
        }

        newShapeWidth = round(newShapeWidth)
        newShapeHeight = round(newShapeHeight)

        if (shape.type === RATING_TYPE && shape.width && shape.height) {
            newFontSize = getRatingShapeFontSize(shape as RatingShape, newShapeWidth, newShapeHeight)
            const c = newShapeHeight / shape.height
            newCornerRadius = shape.corner_radius ? round(shape.corner_radius * c) : shape.corner_radius
            newTextPadding = shape.text_padding ? round(shape.text_padding * c) : shape.text_padding
        }

        if (newShapeWidth <= 1) {
            newShapeWidth = 20
        }

        if (newShapeHeight <= 1) {
            newShapeHeight = 10 //min size
        }

        this.props.change(formName, subFormName, {
            ...shape,
            width: newShapeWidth,
            height: newShapeHeight,
            text_wrap: textWrap,
            text_fontsize: newFontSize,
            text_padding: shape.text_padding ? newTextPadding : null,
            corner_radius: shape.corner_radius ? newCornerRadius : null,
        })
    }

    checkMissingGlyphs = () => {
        const { activeShapeId, imagickFonts, form, isAlt } = this.props

        const shape: Shape = form.shapes[activeShapeId]

        if (!shape || !imagickFonts.isLoaded || !shape.text_fontfamily || (!shape.text_content_replaced && !shape.text_content)) {
            return null
        }

        const font = find(imagickFonts.data.fontsMap, (font: ImagickFont) => {
            return `${font.text_fontfamily}` === `${shape.text_fontfamily}`
        })

        if (!font || !font.file) {
            return null
        }

        const textContent = shape.text_content_replaced ? shape.text_content_replaced : shape.text_content

        let missingGlyphs = []

        opentype.load(`/fie-fonts/${font.file}`, (err: any, font: { glyphs: { length: number; glyphs: { unicodes: any }[] } }) => {
            var allcodes = [];
            if (font?.glyphs) {
                for (var i = 0; i < font.glyphs.length; i++) {
                    allcodes = allcodes.concat(font.glyphs.glyphs[i].unicodes || []);
                }

                for (let i = 0; i < textContent.length; i++) {
                    const intChar = shape.text_content.charCodeAt(i)

                    if (!allcodes.includes(intChar) && shape.text_content[i] !== "\n") {
                        missingGlyphs.push(shape.text_content[i])
                    }
                }

                //if (Array.isArray(missingGlyphs) && missingGlyphs.length > 0) {
                    //console.debug(`there are some missing glyphs in ${shape.text_fontfamily}`, missingGlyphs)
                //}
            }

            this.setState({ missingGlyphs: missingGlyphs })
        })
    }

    render() {
        const { project, activeShapeId, form, isAlt } = this.props
        const { missingGlyphs, showDepotsWarning } = this.state

        const formName = getFormName(isAlt)

        const shape: Shape = form.shapes[activeShapeId]

        if (!shape) {
            return <div />
        }

        const subFormName = `shapes.${activeShapeId}`

        const templateSize = getTemplateSize(form)

        const allowTextWrap = [CIRCLE_TYPE, TEXT_TYPE, RECTANGLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, DYNAMIC_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, TEXT_INSIDE_SHAPE_TYPE].includes(shape.type)
        const allowTextContent = [CIRCLE_TYPE, TEXT_TYPE, RECTANGLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, CURVED_TEXT_TYPE, CIRCLE_TEXT_TYPE, TEXT_INSIDE_SHAPE_TYPE].includes(shape.type)
        const allowFontOptions = [CIRCLE_TYPE, ELLIPSE_TYPE, RECTANGLE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, TEXT_TYPE, DYNAMIC_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, TEXT_INSIDE_SHAPE_TYPE, CURVED_TEXT_TYPE, CIRCLE_TEXT_TYPE].includes(shape.type)
        const dynamicXMLElement = [DYNAMIC_TEXT_TYPE, IMAGE_FROM_ELEMENTS_TYPE, RATING_TYPE].includes(shape.type)

        const userId = project.user && project.user.id ? project.user.id : null

        return (
            <React.Fragment>
                <Grid cols={'auto'} gap={'5px'} style={{ padding: '10px 0px' }}>
                    {allowTextContent &&
                        <TextContentForm
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                            setEditMode={this.props.setEditMode}
                        />
                    }

                    {shape.type === DYNAMIC_TEXT_TYPE &&
                        <XmlElementSelectForm
                            fieldName={'text_xml_element'}
                            shape={shape}
                            formName={formName}
                            activeShapeId={activeShapeId}
                            project={project}
                            randomProduct={this.props.randomProduct}
                            change={this.props.change}
                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                        />
                    }
                    {shape.type === IMAGE_FROM_ELEMENTS_TYPE &&
                        <React.Fragment>
                        <XmlElementSelectForm
                            fieldName={'image_xml_element'}
                            shape={shape}
                            formName={formName}
                            activeShapeId={activeShapeId}
                            project={project}
                            randomProduct={this.props.randomProduct}
                            change={this.props.change}
                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                            />

                            <ImageFromElementForm
                                formName={formName}
                                randomProduct={this.props.randomProduct}
                                change={this.props.change}
                                shape={shape}
                            />
                        </React.Fragment>
                    }

                    {shape.type === RATING_TYPE &&
                        <React.Fragment>
                            <Grid cols={'auto auto'} gap={'5px'} style={{ ' .muk-gridcell': { alignSelf: 'center' } }}>
                                <GridCell>{trans('rating_source')}</GridCell>
                                <GridCell>
                                    <InputGroup
                                        singleChoice={true}
                                        hideInput={true}
                                        name={`${formName}.shapes.${shape.index}.rating_from_depots`}
                                        items={[
                                            { value: 0, label: trans('xml_element') },
                                            { value: 1, label: trans('depots_extension') },
                                        ]}
                                        style={{
                                            ' .m-radio-wrapper': {
                                                margin: '3px',
                                            },
                                            background: '#f0f1f5',
                                            borderRadius: '8px',
                                        }}
                                        /* onChange={(value) => this.checkDepots(value)} */
                                    />
                                </GridCell>

                                {!!(shape.rating_from_depots && showDepotsWarning) &&
                                    <GridCell col={'1/-1'}>
                                        <Message
                                            type="warning"
                                            icon={'exclamation-triangle'}
                                            text={trans('depots_not_enabled')}
                                        />
                                    </GridCell>
                                }

                                {!shape.rating_from_depots &&
                                    <React.Fragment>
                                        <GridCell>{trans('rating_xml_element')}</GridCell>
                                        <XmlElementSelectForm
                                            fieldName={'rating_xml_element'}
                                            shape={shape}
                                            formName={formName}
                                            activeShapeId={activeShapeId}
                                            project={project}
                                            randomProduct={this.props.randomProduct}
                                            change={this.props.change}
                                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                                        />

                                        <GridCell>{trans('rating_count_xml_element')}</GridCell>
                                        <XmlElementSelectForm
                                            fieldName={'rating_count_xml_element'}
                                            shape={shape}
                                            formName={formName}
                                            activeShapeId={activeShapeId}
                                            project={project}
                                            randomProduct={this.props.randomProduct}
                                            change={this.props.change}
                                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                                        />

                                        {/* some users only */}
                                        {testUsers.includes(userId) &&
                                            <React.Fragment>
                                                <GridCell className="some-users-only">{trans('rating_max_value')}</GridCell>
                                                <GridCell className="some-users-only">
                                                    <NumericInput
                                                        formName={formName}
                                                        fieldName={'rating_max_value'}
                                                        shapeIndex={shape.index}
                                                        id={'rating_max_value'}
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        value={round(shape.rating_max_value)}
                                                    />
                                                </GridCell>
                                            </React.Fragment>
                                        }
                                    </React.Fragment>
                                }
                            </Grid>
                        </React.Fragment>
                    }

                    {allowFontOptions &&
                        <React.Fragment>
                            {missingGlyphs && missingGlyphs.length > 0 && shape.type !== RATING_TYPE &&
                                <GridCell col={'1/-1'} className="warnings">
                                    <Message type="warning" icon={<Icon type="exclamation-triangle" color={Theme.red} size={30} />} closeable={false}>
                                        <div>
                                            {trans('missing_symbols')} {capitalize(trans('text_font'))}: <b>{shape.text_fontfamily}</b>, {trans('characters')}: {missingGlyphs}
                                        </div>
                                    </Message>
                                </GridCell>
                            }

                            <ImagickFontSelect
                                currentFontFamily={shape.text_fontfamily}
                                setFontFamily={(value: string) => {
                                    this.props.change(formName, `shapes.${activeShapeId}.text_fontfamily`, value)
                                    this.updateShapeSizeDebounced(subFormName, activeShapeId, true)
                                }}
                                sampleText={(shape.text_content_replaced) ? shape.text_content_replaced : (shape.text_content ?? trans("font_example"))}
                            />

                            <TextOptionsForm
                                shape={shape}
                                activeShapeId={activeShapeId}
                                formName={formName}
                                change={this.props.change}
                                updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                            />
                        </React.Fragment>
                    }

                    {dynamicXMLElement &&
                        <XmlElementFormatterForm
                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                        />
                    }

                    {shape.type === RATING_TYPE &&
                        <RatingForm
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                            updateShapeSizeDebounced={this.updateShapeSizeDebounced}
                        />
                    }

                    {shape.type === TEXT_WITH_ELEMENTS_TYPE &&
                        <TextWithElementsForm
                            formName={formName}
                            shape={shape}
                            change={this.props.change}
                            subFormName={`${subFormName}.text_content`}
                            project={project}
                        />
                    }
                </Grid>

                <Grid cols={"20% 80%"} gap={'5px'}>
                    {allowFontOptions && shape.type !== RATING_TYPE &&
                        <TextOptionsTwoForm
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                        />
                    }

                    {shape.type !== RATING_TYPE &&
                        <ShapeStrokeLinesAndColorsForm
                            form={form}
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                            handleChangeGradient={this.handleChangeGradient}
                        />
                    }

                    {shape.type !== RATING_TYPE &&
                        <ShapeStrokeForm
                            form={form}
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                            handleChangeGradient={this.handleChangeGradient}
                        />
                    }

                    {allowTextWrap &&
                        <GridCell col={'1/-1'}>
                            <GridCell title={trans('text_wrap')}>
                                <Grid cols={'auto max-content'} gap={'20px'}>
                                    <GridCell onClick={() => this.props.change(formName, `shapes.${activeShapeId}.text_wrap`, !shape.text_wrap)} style={{ alignSelf: 'center', cursor: 'pointer' }}>{trans('text_wrap')}</GridCell>
                                    <GridCell style={{ alignSelf: 'center' }}>
                                        <StyledToggler
                                            value={shape.text_wrap || false}
                                            onChange={(e: any) => this.props.change(formName, `shapes.${activeShapeId}.text_wrap`, e)}
                                            disabled={!!shape.text_orientation}
                                        />
                                    </GridCell>
                                </Grid>
                            </GridCell>
                        </GridCell>
                    }

                    {/* some users only, see FIE-882 */}
                    {testUsers.includes(userId) && [BITMAP_TYPE, CUSTOM_SET_TYPE, ...TEXT_SHAPE_TYPES].includes(shape.type) &&
                        <GridCell col={'1/-1'}>
                            <GridCell title={trans('aspect_size_ratio')} className="some-users-only">
                                <Grid cols={'auto max-content'} gap={'20px'}>
                                    <GridCell
                                        onClick={() => this.props.change(formName, `shapes.${shape.index}.is_aspect_size_ratio`, !shape.is_aspect_size_ratio)}
                                        style={{ alignSelf: 'center', cursor: 'pointer' }}>
                                        {trans('aspect_size_ratio')}
                                    </GridCell>
                                    <GridCell style={{ alignSelf: 'center' }}>
                                        <StyledToggler
                                            value={shape.is_aspect_size_ratio}
                                            onChange={(e) => this.props.change(formName, `shapes.${shape.index}.is_aspect_size_ratio`, e)}
                                        />
                                    </GridCell>
                                </Grid>
                            </GridCell>
                        </GridCell>
                    }

                    {![FRAME_TYPE, DASHED_FRAME_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE, BRUSH_TYPE, CUSTOM_SET_TYPE].includes(shape.type) &&
                        <React.Fragment>
                            <ReflectionForm
                                shape={shape}
                                formName={formName}
                                change={this.props.change}
                                activeShapeId={activeShapeId}
                            />
                            <ShadowForm
                                formName={formName}
                                shape={shape}
                                change={this.props.change}
                                activeShapeId={activeShapeId}
                            />
                        </React.Fragment>
                    }

                    {[BITMAP_TYPE, IMAGE_TYPE, BRUSH_TYPE, BADGE_TYPE, BADGE1_TYPE, CIRCLE_TEXT_TYPE, DASHED_FRAME_TYPE, FRAME_TYPE].includes(shape.type) &&
                        <React.Fragment>
                            <GridCell>{trans('opacity')}</GridCell>
                            <GridCell>
                                <StyledSliderDiv>
                                    <Slider
                                        value={round(shape.opacity || 100)}
                                        onChange={(event: Event, value: number) => this.props.change(formName, `shapes.${shape.index}.opacity`, value)}
                                        step={1}
                                        min={0}
                                        max={100}
                                        valueLabelDisplay="auto"
                                        getAriaLabel={() => trans('opacity')}
                                        getAriaValueText={(value) => `${value}`}
                                        valueLabelFormat={(value) => `${value}`}
                                    />
                                </StyledSliderDiv>
                            </GridCell>
                        </React.Fragment>
                    }

                    {![BITMAP_TYPE, IMAGE_TYPE, BRUSH_TYPE, BADGE_TYPE, BADGE1_TYPE, CUSTOM_SET_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE /* , RATING_TYPE */].includes(shape.type) &&
                        <ShapePositionSizeAndRotationForm
                            formName={formName}
                            shape={shape}
                            activeShapeId={activeShapeId}
                            change={this.props.change}
                            templateSize={templateSize}
                        />
                    }
                </Grid>
            </React.Fragment>
        )
    }
}

export const StyledToggler = css(Toggler)({
    ' .muk-grid': {
        paddingTop: '5px',
        marginBottom: '20px',
    },
})

export const StyledSeparator = css('hr')({
    margin: '20px 0',
    borderColor: '#f1eed7',
    opacity: .5,
})

export const StyledSeparatorNoLine = css('hr')({
    border: 0,
    paddingBottom: '20px',
})

export const StyledSliderDiv = css('div')({
    position: 'relative',
    margin: '25px 0 10px 0',
    padding: '0 25px',
    ' .MuiSlider-colorSecondary': {
        color: iconDisabledColor,
        ' .MuiSlider-track': {
            backgroundColor: iconDisabledColor,
            borderColor: iconDisabledColor,
        }
    },
})

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        brushForm: selectFormValues(state, 'brushForm'),
        activeShapeId: state.activeShapeId,
        randomProduct: state.randomProduct,
        project: state.project.data,
        imagickFonts: state.imagickFonts,
    }
}

export default connect(mapStateToProps, {
    change,
    initForm,
    createToast,
    setEditMode,
})(CommonShapeForm)